// Security review: bind writes to the current wallet and distinguish cancelled/replaced receipts.
const FUNCTIONS = Object.freeze({ mint: 'mint', check_in: 'checkIn', map: 'mapMe', workflow: 'generateWorkflow', wallet_check_in: 'checkIn', wallet_map: 'mapMe', wallet_workflow: 'generateWorkflow', wallet_deploy: 'deployAgent' });
const INPUT_TYPES = Object.freeze({ mint: ['uint16', 'bytes32', 'uint256', 'uint48', 'bytes'], check_in: ['uint256'], map: ['uint256'], workflow: ['uint256', 'bytes32'], wallet_check_in: [], wallet_map: [], wallet_workflow: [], wallet_deploy: [] });

export function validateIntent(intent, txType, expectedChainId, expectedContracts = null) {
  if (!Object.hasOwn(FUNCTIONS, txType) || intent?.functionName !== FUNCTIONS[txType]) throw new Error('Unsupported transaction intent');
  if (expectedContracts && intent.address?.toLowerCase() !== expectedContracts[txType === 'mint' ? 'AgentPassport' : 'CortisEngagement']?.toLowerCase()) throw new Error('Intent contract does not match the reviewed deployment');
  if (Number(intent.chainId) !== expectedChainId) throw new Error('Intent is for a different network');
  if (!/^0x[0-9a-fA-F]{40}$/.test(intent.address) || !Array.isArray(intent.args) || !Array.isArray(intent.abi)) throw new Error('Invalid transaction intent');
  if (intent.value != null && BigInt(intent.value) !== 0n) throw new Error('This action must not transfer native currency');
  const functions = intent.abi.filter((item) => item.type === 'function');
  if (functions.length !== 1 || functions[0].name !== FUNCTIONS[txType] || JSON.stringify(functions[0].inputs?.map((input) => input.type)) !== JSON.stringify(INPUT_TYPES[txType])) throw new Error('Intent ABI does not match the requested action');
}

export async function sendWalletIntent({ intent, txType, agentId, expectedChainId, expectedContracts, wallet, publicClient, api, assertIdentity }) {
  validateIntent(intent, txType, expectedChainId, expectedContracts);
  await assertIdentity();
  const hash = await wallet.writeContract({
    address: intent.address, abi: intent.abi, functionName: intent.functionName, args: intent.args,
    account: wallet.account, chain: wallet.chain, value: 0n,
  });
  // A transaction may already be broadcast when account access changes. Never
  // register it under the next owner's cookie or announce its result there.
  try { await assertIdentity(); }
  catch (error) { error.txHash = hash; error.message = `Transaction ${hash} was broadcast before the wallet changed. Check it before retrying.`; throw error; }
  const register = async (txHash) => {
    for (let attempt = 0; ; attempt += 1) {
      await assertIdentity();
      try { return await api.registerTransaction({ txHash, txType, agentId }); }
      catch (error) {
        if (error.status !== 503 || attempt >= 2) throw error;
        await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
      }
    }
  };
  let transactionId = null;
  let registrationError = null;
  try { ({ transactionId } = await register(hash)); }
  catch (error) {
    if (error.code === 'STALE_SESSION' || error.code === 'SESSION_NOT_READY') throw error;
    registrationError = 'Transaction broadcast; activity registration needs retry.';
  }

  let replacement = null;
  let receipt;
  try {
    receipt = await publicClient.waitForTransactionReceipt({
      hash,
      onReplaced: (event) => { replacement = event; },
    });
  } catch {
    const error = new Error(`Transaction ${hash} was broadcast; its receipt is currently unavailable. Check the transaction before retrying.`);
    error.code = 'RECEIPT_UNAVAILABLE';
    error.txHash = hash;
    throw error;
  }
  await assertIdentity();
  const confirmedHash = receipt.transactionHash;
  if (!confirmedHash || receipt.blockNumber == null || !['success', 'reverted'].includes(receipt.status)) throw new Error('RPC returned an invalid transaction receipt');
  if (replacement && replacement.reason !== 'repriced') {
    throw new Error('The transaction was cancelled or replaced by a different action. The original action is not confirmed.');
  }
  if (confirmedHash.toLowerCase() !== hash.toLowerCase()) {
    if (replacement?.reason !== 'repriced') throw new Error('Receipt does not match the submitted transaction');
    try { ({ transactionId } = await register(confirmedHash)); registrationError = null; }
    catch (error) {
      if (error.code === 'STALE_SESSION' || error.code === 'SESSION_NOT_READY') throw error;
      transactionId = null;
      registrationError = 'Replacement confirmed; activity registration needs retry.';
    }
  }
  if (confirmedHash.toLowerCase() === hash.toLowerCase() && registrationError) {
    try { ({ transactionId } = await register(hash)); registrationError = null; }
    catch (error) {
      if (error.code === 'STALE_SESSION' || error.code === 'SESSION_NOT_READY') throw error;
    }
  }
  const record = transactionId ? await api.transaction(transactionId).catch(() => null) : null;
  await assertIdentity();
  return { hash: confirmedHash, transactionId, status: receipt.status, blockNumber: receipt.blockNumber.toString(), record, registrationError };
}
